import {
    Card,
    CardContent,
    CardHeader,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Switch,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {stringToHexColor} from "../util/util";
import React, {useState} from "react";
import BarChartIcon from '@mui/icons-material/BarChart';
import SsidChartIcon from '@mui/icons-material/SsidChart';

export const CameraDashboardTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{p: 1}}>
                <Typography variant='overline' key='datetime'>{payload[0].payload.toolTipTime}</Typography>
                {payload.map((item) => (
                    <>
                        <Typography variant='subtitle2' sx={{display: 'block', mt: 0.4}}>{item.name}:</Typography>
                        {(typeof item.payload[item.name] === 'string' || typeof item.payload[item.name] === 'number') ?
                            <Typography
                                variant='caption'
                                key={item.name}
                                sx={{display: 'block'}}
                            >
                                {item.payload[item.name]}
                            </Typography> :
                            Object.entries(item.payload[item.name]).map(([key, value]) => (
                                <Typography
                                    variant='caption'
                                    key={key}
                                    sx={{display: 'block'}}
                                >
                                    {key} : {typeof value === 'number' ? parseFloat(value.toFixed(2)) : value}
                                </Typography>
                            ))}
                    </>
                ))}
            </Paper>
        );
    }

    return null;
};

export default function VisualizationChart({data, dataKey, title, valueKey, classKey}) {
    const [chartType, setChartType] = useState('bar');
    const classData = data.map(datum => (Object.assign({
        dateKey: datum.dateKey,
        toolTipTime: datum.toolTipTime
    }, ...classKey.map(k => (datum?.[k] && typeof datum[k] === 'object' && !Array.isArray(datum[k])) ? datum[k] : {}))));

    const classDataKey = classKey.flatMap(k => Array.isArray(dataKey?.[k]) ? dataKey[k] : (dataKey?.[k] ?? []));
    const [visibleKeys, setVisibleKeys] = useState(classDataKey.reduce((acc, item) => ({...acc, [item]: true}), {}));

    const handleSelectionChange = (event) => {
        const newVisibleKeys = {
            ...visibleKeys,
            [event.target.name]: event.target.checked,
        }
        setVisibleKeys(newVisibleKeys);
    };

    const handleChartType = () => {
        setChartType((type) => (type === 'bar' ? 'line' : 'bar'));
    }

    return <Card key='chart-card'
                 sx={{borderRadius: '16px', width: '100%', height: '100%', padding: 2, paddingBottom: 0}}>
        <CardHeader
            title={title}
            action={<ToggleButtonGroup
                exclusive
                value={chartType}
                onChange={handleChartType}
            >
                <ToggleButton value="bar">
                    <BarChartIcon/>
                </ToggleButton>
                <ToggleButton value="line">
                    <SsidChartIcon/>
                </ToggleButton>
            </ToggleButtonGroup>}
        />
        <CardContent>
            <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <ResponsiveContainer width="100%" minHeight={500}>
                        {chartType === 'bar' ?
                            <BarChart data={classData} minWidth={450} minHeight={300} barCategoryGap='15%'>
                                <CartesianGrid stroke="#ccc" strokeDasharray="3 3"/>
                                <XAxis dataKey="dateKey" padding={{left: 10, right: 10}}/>
                                <YAxis/>
                                <Tooltip content={<CameraDashboardTooltip/>}/>
                                {classDataKey.map((dataKey) => (
                                    <Bar
                                        key={dataKey}
                                        name={dataKey}
                                        dataKey={(row) => (((valueKey && row[dataKey]) ? row[dataKey][valueKey] : row[dataKey]) ?? null)}
                                        maxBarSize={80}
                                        fill={stringToHexColor(dataKey, true)}
                                        hide={!visibleKeys[dataKey]}
                                    />
                                ))}
                            </BarChart> :
                            <LineChart data={classData} minWidth={450} minHeight={300}>
                                <CartesianGrid stroke="#ccc" strokeDasharray="3 3"/>
                                <XAxis dataKey="dateKey" padding={{left: 10, right: 10}}/>
                                <YAxis/>
                                <Tooltip content={<CameraDashboardTooltip/>}/>
                                {classDataKey.map((dataKey) => (
                                    <Line
                                        key={dataKey}
                                        name={dataKey}
                                        dataKey={(row) => (((valueKey && row[dataKey]) ? row[dataKey][valueKey] : row[dataKey]) ?? null)}
                                        type="monotone"
                                        dot={false}
                                        stroke={stringToHexColor(dataKey)}
                                        strokeWidth={2}
                                        hide={!visibleKeys[dataKey]}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>}
                    </ResponsiveContainer>
                </Grid>
                <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <FormGroup row sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                        {classDataKey.map((key) => (
                            <FormControlLabel
                                control={<Switch/>}
                                checked={visibleKeys[key] || false}
                                onChange={handleSelectionChange}
                                name={key}
                                label={key}
                                key={key}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: stringToHexColor(key, true),
                                        '&:hover': {
                                            backgroundColor: stringToHexColor(key, true, 0.40),
                                        },
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: stringToHexColor(key, true),
                                    },
                                }}
                            />
                        ))}
                    </FormGroup>
                </Grid>
                <Grid size={12} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <Typography variant="overline" gutterBottom>all timestamps are in user timezone</Typography>
                </Grid>
            </Grid>
        </CardContent>
    </Card>
}